import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, MessageSquarePlus, CheckCircle2, XCircle, HelpCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const CHECKLIST = [
  { key: "id_document_verified", label: "ID document verified", required: true },
  { key: "name_matches_profile", label: "Name matches profile", required: true },
  { key: "address_matches_profile", label: "Address matches profile", required: false },
  { key: "income_document_verified", label: "Income document verified", required: true },
  { key: "bank_account_verified", label: "Bank account verified", required: false },
  { key: "ekyc_verified", label: "eKYC verified", required: true },
  { key: "required_documents_complete", label: "Required documents complete", required: true },
  { key: "fraud_flags_checked", label: "Fraud red flags checked", required: false },
  { key: "calculator_terms_reviewed", label: "Loan calculator terms reviewed", required: false },
  { key: "borrower_consent_confirmed", label: "Borrower consent confirmed", required: true },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ApplicationReview() {
  const { id } = useParams();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [cl, setCl] = useState({});
  const [noteText, setNoteText] = useState("");
  const [actionNote, setActionNote] = useState("");
  const [docReview, setDocReview] = useState({});
  const [bank, setBank] = useState(null);
  const [decisionNote, setDecisionNote] = useState("");
  const [overrideReason, setOverrideReason] = useState("");

  const load = async () => {
    const { data } = await api.get(`/applications/${id}`);
    setA(data);
    setCl(data.checklist || {});
    setBank(data.bank_nomination || null);
  };
  useEffect(() => { load(); }, [id]);

  const saveChecklist = async () => {
    try { const { data } = await api.put(`/applications/${id}/checklist`, cl); setCl(data); toast.success("Checklist saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const doAction = async (action) => {
    try { await api.post(`/applications/${id}/reviewer-action`, { action, note: actionNote }); toast.success(`Action: ${action}`); setActionNote(""); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    await api.post(`/applications/${id}/notes`, { note: noteText });
    setNoteText(""); load();
  };

  const reviewDoc = async (docId) => {
    const v = docReview[docId];
    if (!v?.status) return toast.error("Select status");
    await api.patch(`/documents/${docId}/review`, { status: v.status, comment: v.comment || "" });
    toast.success("Document reviewed");
    load();
  };

  const adminDecide = async (decision) => {
    try {
      await api.post(`/applications/${id}/decision`, { decision, note: decisionNote, override_reason: overrideReason || null });
      toast.success(`Decision: ${decision}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!a) return <Layout><div className="text-slate-500">Loading…</div></Layout>;

  return (
    <Layout>
      <div className="fade-in grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card className="rounded-xl border-slate-200"><CardContent className="p-6">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Application</div>
                <h1 className="text-2xl font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>{a.product_name}</h1>
                <div className="text-sm text-slate-600">{a.borrower_email} · ${a.amount.toFixed(2)} · {a.duration} {a.duration_unit}</div>
              </div>
              <StatusBadge status={a.status}/>
            </div>
            <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
              <Cell label="Submitted" value={a.submitted_at ? new Date(a.submitted_at).toLocaleString() : "—"}/>
              <Cell label="eKYC" value={a.ekyc?.status || "not_started"}/>
              <Cell label="Total payable" value={`$${a.calculator_snapshot?.total_payable?.toFixed(2)}`}/>
              <Cell label="APR" value={`${a.calculator_snapshot?.effective_apr}%`}/>
              <Cell label="Schedule" value={`${a.calculator_snapshot?.n_payments} × $${a.calculator_snapshot?.per_payment?.toFixed(2)}`}/>
              <Cell label="Frequency" value={a.repayment_frequency}/>
            </div>
          </CardContent></Card>

          <Card className="rounded-xl border-slate-200"><CardContent className="p-6">
            <h3 className="font-semibold text-slate-900" style={{fontFamily:'Outfit'}}>Documents</h3>
            <ul className="mt-3 divide-y divide-slate-100 border border-slate-200 rounded-md">
              {(a.documents||[]).map((d) => (
                <li key={d.id} className="px-4 py-3 space-y-2" data-testid={`review-doc-${d.id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-medium text-slate-900">{d.doc_type}</div>
                      <div className="text-xs text-slate-500">{d.filename}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={d.status}/>
                      <a href={`${BACKEND_URL}/api/documents/${d.id}/download`} target="_blank" rel="noreferrer">
                        <Button size="sm" variant="outline" data-testid={`download-doc-${d.id}`}><Download className="h-4 w-4"/></Button>
                      </a>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-2">
                    <Select value={docReview[d.id]?.status || ""} onValueChange={(v) => setDocReview({...docReview, [d.id]: {...(docReview[d.id]||{}), status: v}})}>
                      <SelectTrigger data-testid={`doc-status-${d.id}`}><SelectValue placeholder="Set status…"/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="verified">Verified</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                        <SelectItem value="needs_reupload">Needs re-upload</SelectItem>
                      </SelectContent>
                    </Select>
                    <Textarea placeholder="Comment" rows={1} data-testid={`doc-comment-${d.id}`} value={docReview[d.id]?.comment || ""} onChange={(e) => setDocReview({...docReview, [d.id]: {...(docReview[d.id]||{}), comment: e.target.value}})}/>
                    <Button onClick={() => reviewDoc(d.id)} data-testid={`doc-review-save-${d.id}`} className="bg-blue-900 hover:bg-blue-800">Save review</Button>
                  </div>
                </li>
              ))}
              {(!a.documents || a.documents.length === 0) && <li className="px-4 py-6 text-sm text-slate-500">No documents uploaded.</li>}
            </ul>
          </CardContent></Card>

          {bank && (
            <Card className="rounded-xl border-slate-200"><CardContent className="p-6">
              <h3 className="font-semibold text-slate-900" style={{fontFamily:'Outfit'}}>Bank nomination</h3>
              <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
                <Cell label="Bank" value={bank.bank_name}/>
                <Cell label="Holder" value={bank.account_holder_name}/>
                <Cell label="Account" value={bank.account_number_masked}/>
                <Cell label="Type" value={bank.account_type}/>
                <Cell label="Transit" value={bank.transit_number}/>
                <Cell label="Institution" value={bank.institution_number}/>
                <Cell label="Name match" value={bank.name_match ? "yes" : "no — flagged"}/>
              </div>
            </CardContent></Card>
          )}

          <Card className="rounded-xl border-slate-200"><CardContent className="p-6 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900" style={{fontFamily:'Outfit'}}>Reviewer notes</h3>
            </div>
            <div className="flex gap-2">
              <Textarea data-testid="note-input" value={noteText} onChange={(e)=>setNoteText(e.target.value)} placeholder="Add an internal note…" rows={2}/>
              <Button onClick={addNote} className="bg-slate-900 hover:bg-slate-800" data-testid="add-note-btn"><MessageSquarePlus className="h-4 w-4"/></Button>
            </div>
            <ul className="space-y-2">
              {(a.notes||[]).map((n) => (
                <li key={n.id} className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-sm" data-testid={`note-${n.id}`}>
                  <div className="text-xs text-slate-500">{n.reviewer_email} · {n.action} · {new Date(n.created_at).toLocaleString()}</div>
                  <div className="text-slate-800">{n.note}</div>
                </li>
              ))}
            </ul>
          </CardContent></Card>
        </div>

        <div className="space-y-4">
          <Card className="rounded-xl border-slate-200"><CardContent className="p-6">
            <h3 className="font-semibold text-slate-900" style={{fontFamily:'Outfit'}}>Approval checklist</h3>
            <ul className="mt-3 space-y-2">
              {CHECKLIST.map((c) => (
                <li key={c.key} className="flex items-start gap-2 text-sm">
                  <Checkbox data-testid={`checklist-${c.key}`} checked={!!cl[c.key]} onCheckedChange={(v) => setCl({...cl, [c.key]: !!v})}/>
                  <span>{c.label}{c.required && <span className="text-red-500 ml-0.5">*</span>}</span>
                </li>
              ))}
            </ul>
            <Button className="mt-4 w-full bg-blue-900 hover:bg-blue-800" onClick={saveChecklist} data-testid="save-checklist-btn">Save checklist</Button>
          </CardContent></Card>

          {user.role === "reviewer" && (
            <Card className="rounded-xl border-slate-200"><CardContent className="p-6 space-y-3">
              <h3 className="font-semibold text-slate-900" style={{fontFamily:'Outfit'}}>Reviewer action</h3>
              <Textarea placeholder="Note for borrower / admin" value={actionNote} onChange={(e)=>setActionNote(e.target.value)} data-testid="action-note"/>
              <div className="grid grid-cols-1 gap-2">
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => doAction("recommend_approval")} data-testid="recommend-approval-btn"><CheckCircle2 className="h-4 w-4 mr-1"/> Recommend approval</Button>
                <Button variant="outline" onClick={() => doAction("request_info")} data-testid="request-info-btn"><HelpCircle className="h-4 w-4 mr-1"/> Request more info</Button>
                <Button className="bg-red-600 hover:bg-red-700" onClick={() => doAction("reject")} data-testid="reject-btn"><XCircle className="h-4 w-4 mr-1"/> Reject</Button>
              </div>
            </CardContent></Card>
          )}

          {user.role === "admin" && (
            <Card className="rounded-xl border-slate-200"><CardContent className="p-6 space-y-3">
              <h3 className="font-semibold text-slate-900" style={{fontFamily:'Outfit'}}>Admin decision</h3>
              <Textarea placeholder="Decision note" value={decisionNote} onChange={(e)=>setDecisionNote(e.target.value)} data-testid="decision-note"/>
              {a.status !== "recommended_for_approval" && (
                <div>
                  <label className="text-xs text-amber-700">Override reason (required to approve without reviewer recommendation)</label>
                  <Textarea value={overrideReason} onChange={(e)=>setOverrideReason(e.target.value)} data-testid="override-reason"/>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => adminDecide("approve")} data-testid="admin-approve-btn">Approve</Button>
                <Button className="bg-red-600 hover:bg-red-700" onClick={() => adminDecide("reject")} data-testid="admin-reject-btn">Reject</Button>
              </div>
              {a.status === "disbursement_pending" && (
                <Button className="w-full bg-blue-900 hover:bg-blue-800" data-testid="mark-disbursed-btn" onClick={async () => { await api.post(`/applications/${id}/disburse`); toast.success("Disbursed"); load(); }}>Mark as disbursed</Button>
              )}
            </CardContent></Card>
          )}
        </div>
      </div>
    </Layout>
  );
}

function Cell({label, value}) {
  return (
    <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-slate-900 font-medium capitalize">{String(value ?? "—").replaceAll('_',' ')}</div>
    </div>
  );
}
